import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useRouteError } from 'react-router-dom'
import { Box, Button, CssBaseline, Grid, ThemeProvider, Typography } from '@mui/material'

import PublicAppBar from '@/common/components/MainAppBar/PublicAppBar'
import { useToolBoxTreatments } from '@/common/hooks'
import { theme as lightTheme } from '@/common/themes/LightTheme'
import { StrikerThemeOverride } from '@/common/themes/overrides/StrikerThemeOverride'
import { WhitelabelThemeOverride } from '@/common/themes/overrides/WhitelabelThemeOverride'

const classes = {
    sorryImage: {
        width: '700px',
    },
    sorryMessage: {
        gap: 1,
        mb: 3,
    },
}

const GenericErrorPage = () => {
    const { t } = useTranslation()
    const { showLibellulaWhitelabelling, showStrikerWhitelabelling } = useToolBoxTreatments()
    const navigate = useNavigate()

    const error = useRouteError()

    const theme = useMemo(
        () =>
            showLibellulaWhitelabelling
                ? WhitelabelThemeOverride(lightTheme)
                : showStrikerWhitelabelling
                  ? StrikerThemeOverride(lightTheme)
                  : lightTheme,
        [showLibellulaWhitelabelling, showStrikerWhitelabelling]
    )

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <PublicAppBar />
            <Grid
                component="section"
                flex={1}
                justifyContent="center"
                size={12}
                container
            >
                <Box
                    sx={[
                        {
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            p: 12,
                        },
                    ]}
                >
                    <Box
                        alt="Sorry"
                        component="img"
                        src="sorry.svg"
                        sx={classes.sorryImage}
                    />

                    <Box
                        sx={[
                            {
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            },
                            classes.sorryMessage,
                        ]}
                    >
                        <Typography variant="h4">{t('Something went wrong.')}</Typography>
                        <Typography variant="body2">
                            {t("It's not you, it's us, please try that again, or contact support for help.")}
                        </Typography>
                    </Box>

                    {error ? (
                        <Box
                            sx={{
                                backgroundColor: 'error.main',
                                borderRadius: 1,
                                color: 'error.contrastText',
                                mb: 3,
                                p: 2,
                            }}
                        >
                            <Typography variant="body2">
                                {t('Error details: {{errorMessage}}', {
                                    errorMessage: error.statusText || error.message || JSON.stringify(error),
                                })}
                            </Typography>

                            {error.fileName ? (
                                <Typography variant="body2">
                                    {t('File:')} {error.fileName}, {t('line:')} {error?.lineNumber}, {t('column:')}{' '}
                                    {error?.columnNumber}
                                </Typography>
                            ) : null}
                        </Box>
                    ) : null}

                    {error ? (
                        <Button
                            color="primary"
                            size="large"
                            variant="contained"
                            onClick={() => window.location.reload()}
                        >
                            {t('Reload the page')}
                        </Button>
                    ) : (
                        <Button
                            color="primary"
                            size="large"
                            variant="contained"
                            onClick={() => navigate('/')}
                        >
                            {t('Back to your dashboard')}
                        </Button>
                    )}
                </Box>
            </Grid>
        </ThemeProvider>
    )
}

export default GenericErrorPage
